/**
 * P2-WORKSPACE D7 — research panel contract (headless).
 *
 * Deliverable 7: "Structured research panel: cached lookup, field-level
 * provenance/confidence/source links, found/not-found/needs-verification
 * outcomes, explicit per-field Apply (research must NEVER silently mutate
 * canonical design record)."
 *
 * Rules enforced by types + functions:
 * - Every research finding carries: outcome (found | not-found |
 *   needs-verification), per-field provenance (source + confidence + link).
 * - Findings are STAGED, never applied implicitly: applying one field is an
 *   explicit designer act that returns an OverrideRecord (which flows through
 *   the versioned override machinery — so the canonical record only changes
 *   through the provenance-bearing path).
 * - Cache: lookups are memoized by (researchKey, query) with a TTL; stale
 *   cache entries are REPORTED, not silently reused.
 */

export const RESEARCH_PANEL_VERSION = '0.1';

export type ResearchOutcome = 'found' | 'not-found' | 'needs-verification';

export type ResearchField =
  | 'skiType'
  | 'length'
  | 'noseWidth'
  | 'tailWidth'
  | 'waistWidthRecommendation'
  | 'flexPattern';

export interface ResearchProvenance {
  source: string;
  sourceUrl?: string;
  /** 0..1 — the research provider's confidence. */
  confidence: number;
  retrievedAtMs: number;
}

export interface ResearchFinding {
  researchKey: string;
  query: string;
  outcome: ResearchOutcome;
  /** Field→suggested value; empty for not-found. */
  suggestions: Partial<Record<ResearchField, number | string>>;
  provenance: Partial<Record<ResearchField, ResearchProvenance>>;
  /** Free-text evidence summary (why this finding says what it says). */
  rationale: string;
}

export interface ResearchCacheEntry {
  finding: ResearchFinding;
  cachedAtMs: number;
  ttlMs: number;
}

/** Simple deterministic TTL cache (no clock reads inside — caller passes now). */
export class ResearchCache {
  private store = new Map<string, ResearchCacheEntry>();

  get(researchKey: string, query: string, nowMs: number):
    { hit: boolean; finding?: ResearchFinding; stale?: boolean } {
    const entry = this.store.get(`${researchKey}::${query}`);
    if (!entry) return { hit: false };
    const fresh = nowMs - entry.cachedAtMs <= entry.ttlMs;
    return { hit: true, finding: entry.finding, stale: !fresh };
  }

  put(finding: ResearchFinding, nowMs: number, ttlMs = 6 * 60 * 60 * 1000): void {
    this.store.set(`${finding.researchKey}::${finding.query}`, {
      finding, cachedAtMs: nowMs, ttlMs,
    });
  }
}

/** Build a not-found finding (explicit, with rationale — never silent). */
export function notFound(researchKey: string, query: string, rationale: string): ResearchFinding {
  return { researchKey, query, outcome: 'not-found', suggestions: {}, provenance: {}, rationale };
}

/**
 * Stage an Apply: convert ONE finding field into an OverrideRecord.
 * The caller applies it via the design-session machinery — canonical state
 * changes ONLY through versioned overrides (never silently).
 */
export function stageApply(input: {
  finding: ResearchFinding;
  field: ResearchField;
  currentValue: number | string;
  by: string;
  at: string;
}): { field: ResearchField; previousValue: number | string; newValue: number | string; reason: string; by: string; at: string } {
  const suggested = input.finding.suggestions[input.field];
  if (suggested === undefined) {
    throw new Error(`finding has no suggestion for ${input.field} (outcome: ${input.finding.outcome})`);
  }
  if (input.finding.outcome === 'not-found') {
    throw new Error('cannot apply a not-found finding');
  }
  const reason = input.finding.outcome === 'needs-verification'
    ? `Research (needs verification): ${input.finding.rationale}`
    : `Research: ${input.finding.rationale}`;
  return {
    field: input.field,
    previousValue: input.currentValue,
    newValue: suggested,
    reason,
    by: input.by,
    at: input.at,
  };
}